import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, Text } from '@machobear/design-system'

import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import isTruthy from '@/utils/is-truthy'
import isVerified from '@/utils/is-verified'
import useMediaQuery from '@/hooks/use-media-query'
import { Anchor } from '@/components/anchor'
import Avatar from '@/components/avatar'
import { Button } from '@/components/button-new'
import CdnImage from '@/components/cdn-image'
import { showLandlordProfileModal } from '@/components/landlord-profile-modal'
import ShowPhoneNumber from '@/components/show-phone-number'
import { Tooltip } from '@/components/tooltip'
import VerifiedBadge from '@/components/verified-badge'

const Landlord = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 140px;
`

const DoubleTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`

const AvatarWrapper = styled.div`
  position: relative;
  display: flex;
  height: 60px;
  width: 80px;
  margin-right: 16px;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: flex-start;
  }
`

const StyledButton = styled(Button)`
  min-width: 110px;
  height: 40px;
  justify-self: flex-end;
  font-weight: ${fontWeights.medium};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const CompanyLink = styled(Anchor)`
  font-size: ${fontSizes.small};
  text-decoration: none;
  width: fit-content;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const InfoText = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  line-height: 22px;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: ${props => props.maxWidth || 250}px;

  ${props =>
    props.allowWrap &&
    `
    overflow: unset;
    white-space: unset;
    text-overflow: unset;
    overflow-wrap: break-word;
  `}

  a {
    color: ${colors.regular};
  }
`

const InfoTextAlt = styled(InfoText)`
  color: ${colors.grey24};
  font-weight: ${fontWeights.regular};
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const LandlordInfo = ({
  landlord,
  isLandlordInfoPresent,
  listings,
  companyLink,
  hideCompanyInfo,
  externalInfo = {},
  ...rest
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const isLandlordVerified = landlord.identity_verified_state === 'verified'
  const displayExternalRealtorInfo = externalInfo.realtor_display_info
  const landlordName = displayExternalRealtorInfo
    ? externalInfo.realtor_name
    : landlord.preferred_name || landlord.first_name
  const showLandlordBio = isLandlordInfoPresent && !displayExternalRealtorInfo

  return (
    <Landlord {...rest}>
      <DoubleTextWrapper>
        <AvatarWrapper>
          {displayExternalRealtorInfo ? (
            <CdnImage
              src={externalInfo.realtor_logo_url}
              sizes="80px"
              layout="fill"
              placeholder="blur"
              objectFit="contain"
              alt={`${landlordName} Logo`}
            />
          ) : (
            <Avatar
              width={56}
              height={56}
              location={landlord.picture}
              shadow
              onClick={
                showLandlordBio
                  ? () => showLandlordProfileModal({ landlord, t })
                  : null
              }
            />
          )}
        </AvatarWrapper>
        <Column>
          <NameWrapper>
            <InfoText maxWidth={isMobile ? 200 : 230}>{landlordName}</InfoText>
            <Tooltip
              text={t(
                'b.listing.view.id_verified.tooltip',
                'This user is ID / Business Licence verified.'
              )}
              position="top"
            >
              <VerifiedBadge
                verified={isVerified(landlord, landlord.company)}
                type="id"
                height={22}
                width={30}
                bottom={-2}
              />
            </Tooltip>
            {!isLandlordVerified && (
              <Box
                background="cautionLight"
                marginLeft="xxsmall"
                paddingX="xxsmall"
                paddingY="xxxsmall"
                borderRadius="small"
              >
                <Text weight="semiBold" size="small">
                  {t(
                    'listing:b.listing.preview.landlord.id_unverified.message',
                    'ID Unverified'
                  )}
                </Text>
              </Box>
            )}
          </NameWrapper>

          {!hideCompanyInfo &&
            landlord.company.name &&
            !displayExternalRealtorInfo && (
              <InfoTextAlt maxWidth={isMobile ? 220 : 270}>
                {landlord.company.name}
              </InfoTextAlt>
            )}
          <ShowPhoneNumber
            isRented={
              listings.state_machine === 'rented' ||
              isTruthy(listings.marked_as_rented)
            }
            hidePhone={landlord.hide_phone_number}
            phoneNum={landlord.phone_number}
          />
        </Column>
      </DoubleTextWrapper>
      <ButtonWrapper>
        {showLandlordBio && (
          <StyledButton
            theme="secondary"
            size="unset"
            onClick={() => showLandlordProfileModal({ landlord, t })}
            id="listing-details-read-bio-click"
          >
            {t('listing:b.listing.view.read_bio.text', 'Read Bio')}
          </StyledButton>
        )}
        {companyLink && (
          <CompanyLink
            href={companyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <StyledButton
              theme="secondary"
              size="unset"
              id="listing-details-view-listings-click"
            >
              {t(
                'listing:b.listing.view.view_all_listings.link',
                'View listings'
              )}
            </StyledButton>
          </CompanyLink>
        )}
      </ButtonWrapper>
    </Landlord>
  )
}

export default LandlordInfo
